import { useLocation, Outlet, Link } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  LogOut,
  Home,
  Users,
  Server,
  User,
  Wrench,
  FileText,
  Menu,
  X,
  ChevronRight,
  Moon,
  Sun,
  Settings,
  Shield,
  ScrollText,
} from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Toaster } from '@/components/ui/toaster';
import { OktaLogo } from '@/components/okta-logo';

interface NavSection {
  title: string;
  items: {
    href: string;
    label: string;
    icon: any;
    featureFlag?: string;
  }[];
}

export const DashboardLayout = () => {
  const location = useLocation();
  const { oktaAuth, authState } = useOktaAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    }
  }, []);

  useEffect(() => {
      apiClient.getPublicConfig().then((cfg) => {
        setFeatureFlags(cfg.feature_flags || {});
      }).catch(() => {});
    }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleLogout = async () => {
    try {
      await oktaAuth.signOut();
      // Disable redirect to login page after logout, as the Okta signOut method will handle redirection
      // window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    }
  };

  const navSections: NavSection[] = [
    {
      title: 'Dashboard',
      items: [
        { href: '/dashboard', label: 'Overview', icon: Home },
      ],
    },
    {
      title: 'Configuration',
      items: [
        { href: '/dashboard/agents', label: 'Agents', icon: Users },
        { href: '/dashboard/resources', label: 'Resources', icon: Server },
        { href: '/dashboard/tools', label: 'Tools', icon: Wrench },
      ],
    },
    {
      title: 'Security & Compliance',
      items: [
        { href: '/dashboard/policies', label: 'Policies', icon: Shield, featureFlag: 'cedarEnabled' },
        { href: '/dashboard/audit', label: 'Audit Log', icon: FileText },
        { href: '/dashboard/event-log', label: 'Event Log', icon: ScrollText },
        { href: '/dashboard/sessions', label: 'User Sessions', icon: LogOut },
      ],
    },
    {
      title: 'Settings',
      items: [
        { href: '/dashboard/settings/dcr', label: 'DCR Policy', icon: Settings },
      ],
    },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
        {!sidebarCollapsed && (
          <div className="flex items-center space-x-3">
            <OktaLogo className="h-8" isDark={theme === 'dark'} />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Okta MCP Bridge</h3>
          </div>
        )}
        {sidebarCollapsed && (
          <div className="mx-auto">
            <OktaLogo className="h-8" isDark={theme === 'dark'} />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-6">
        {navSections.map((section) => (
          <div key={section.title}>
            {!sidebarCollapsed && (
              <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {section.title}
              </h3>
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                if (item.featureFlag) {
                  const negate = item.featureFlag.startsWith('!');
                  const flagName = negate ? item.featureFlag.slice(1) : item.featureFlag;
                  const flagValue = !!featureFlags[flagName];
                  if (flagValue === negate) {
                    return null; // Skip rendering this item if the feature flag condition isn't met
                  }
                }
                const Icon = item.icon;
                const isActive = item.href === '/dashboard'
                  ? location.pathname === item.href
                  : location.pathname.startsWith(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <Icon className={`w-5 h-5 shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t dark:border-gray-700 space-y-2">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleTheme}
          className={`w-full ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`}
          title="Toggle theme"
        >
          {theme === 'light' ? (
            <Moon className={`w-4 h-4 shrink-0 ${sidebarCollapsed ? '' : 'mr-2'}`} />
          ) : (
            <Sun className={`w-4 h-4 shrink-0 ${sidebarCollapsed ? '' : 'mr-2'}`} />
          )}
          {!sidebarCollapsed && <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
        </Button>

        {/* Collapse Toggle - Desktop only */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className={`hidden md:flex w-full ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronRight
            className={`w-4 h-4 shrink-0 transition-transform ${
              sidebarCollapsed ? '' : 'rotate-180 mr-2'
            }`}
          />
          {!sidebarCollapsed && <span>Collapse</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-white dark:bg-gray-800 border-r dark:border-gray-700 transition-all duration-300 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div
        className={`transition-all duration-300 ${
          sidebarCollapsed ? 'md:pl-16' : 'md:pl-64'
        }`}
      >
        {/* Top bar */}
        <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </Button>

              {/* Mobile logo */}
              <div className="flex items-center space-x-3 md:hidden">
                <OktaLogo className="h-7" isDark={theme === 'dark'} />
                <h3 className="text-base font-semibold text-gray-800 dark:text-white">Okta MCP Bridge</h3>
              </div>
            </div>

            {/* User menu */}
            <div className="flex items-center space-x-4">
              {authState?.isAuthenticated && (
                <div className="hidden sm:flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <User className="w-4 h-4 mr-2" />
                  <span>{authState.idToken?.claims.email || authState.idToken?.claims.name}</span>
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      <Toaster />
    </div>
  );
};
